import { useCallback } from 'react';
import type { DeliveryOrderDetail } from '@sg/graphql-schema';

import { useIsLoggedIn } from '@order/AuthMachine';
import { type OrderingContext, useOrdering } from '@order/features/ordering';
import { type PartialLineItem } from '@order/graphql';
import { useFeatureFlag } from '@order/LaunchDarkly';

import { useAddUpsellToBag } from '../useAddUpsellToBag';
import { useBagLoyaltyData } from '../useBagLoyaltyData';
import { useBagLoyaltyRewards } from '../useBagLoyaltyRewards/useBagLoyaltyRewards';
import { useBagPaymentInfo } from '../useBagPaymentInfo';
import { getDefaultTip } from '../useBagTips';
import { useBagTracking } from '../useBagTracking';
import { useCart } from '../useCart';
import { useChangeChannelOrLocation } from '../useChangeChannelOrLocation';
import { useCredits } from '../useCredits';
import { useDefaultPaymentMethod } from '../useDefaultPaymentMethod';
import { useFetchTelemetryData } from '../useFetchTelemetryData';
import { useFetchWantedTimes } from '../useFetchWantedTimes';
import { useGiftCards } from '../useGiftCards';
import { useNavigatePostOrder } from '../useNavigatePostOrder';
import { useOnPostOrder } from '../useOnPostOrder';
import { usePaymentMethods } from '../usePaymentMethods';
import { usePromoCode } from '../usePromoCode';
import { useRequestLogin } from '../useRequestLogin';
import { useRewards } from '../useRewards';
import { useSubmitOrder } from '../useSubmitOrder';
import { useUpdateLineItem } from '../useUpdateLineItem';

/**
 * Hook that binds the ordering machine with the external data.
 */
export const useOrderState = (params: UseOrderStateParams) => {
  const {
    isStripePaymentsSdkEnabled,
    isExpressPaymentsEnabled,
    handleOrderSubmissionResponse,
  } = params;

  const isLoggedIn = useIsLoggedIn();
  const bagTracking = useBagTracking();
  const dynamicTips = useFeatureFlag('CELS-1191-permanent-dynamic-tip-values');

  // ─── Loyalty ──────────────────────────────────────────────────────────────

  const { fetchLoyaltyData } = useBagLoyaltyData();
  const { fetchRewards } = useBagLoyaltyRewards();
  const { applyReward, removeReward } = useRewards();

  // ──────────────────────────────────────────────────────────────────────────

  const { fetchCart } = useCart();
  const { fetchCredits } = useCredits();
  const { fetchWantedTimes } = useFetchWantedTimes();
  const { addUpsellToBag } = useAddUpsellToBag();
  const { fetchGiftCardBalance, applyGiftCard } = useGiftCards(fetchCredits);
  const { fetchBagPaymentInfo } = useBagPaymentInfo();
  const { fetchPaymentMethods, addPaymentMethod } = usePaymentMethods();
  const { fetchDefaultPaymentMethod, setDefaultPaymentMethod } =
    useDefaultPaymentMethod();
  const { updateLineItem } = useUpdateLineItem();
  const { applyPromoCode } = usePromoCode(fetchRewards);
  const { changeChannelOrLocation } = useChangeChannelOrLocation();
  const { requestLogin } = useRequestLogin();
  const { submitOrder } = useSubmitOrder(setDefaultPaymentMethod);
  const { fetchTelemetryData } = useFetchTelemetryData();
  const { navigatePostOrder } = useNavigatePostOrder();
  const { onPostOrder } = useOnPostOrder();

  const fetchInitialData = useCallback(async () => {
    if (!isLoggedIn) {
      return {
        cart: await fetchCart(),
        paymentMethods: [],
        defaultPaymentMethodId: undefined,
        defaultTip: 0,
        availablePoints: 0,
        availableCredit: 0,
        giftCardBalance: 0,
        customerId: '',
        firstName: '',
        lastName: '',
        email: '',
        phoneNumber: '',
        stripeCustomerId: undefined,
        stripeCustomerKey: undefined,
        stripeSession: undefined,
        isNewCustomer: true,
        isOptedOutFromLoyalty: false,
      };
    }

    const [
      cart,
      paymentMethods,
      defaultPaymentMethodId,
      availableCredit,
      giftCardBalance,
      telemetryData,
      loyaltyData,
      bagPaymentInfo,
    ] = await Promise.all([
      fetchCart(),
      fetchPaymentMethods(),
      fetchDefaultPaymentMethod(),
      fetchCredits(),
      fetchGiftCardBalance(),
      fetchTelemetryData(),
      fetchLoyaltyData(),
      fetchBagPaymentInfo(isStripePaymentsSdkEnabled),
    ]);

    const defaultTip = getDefaultTip(
      dynamicTips,
      cart.orderChannel,
      cart.ledger.subtotal,
    );

    return {
      cart,
      paymentMethods,
      defaultPaymentMethodId,
      availableCredit,
      giftCardBalance,
      defaultTip,
      ...bagPaymentInfo,
      ...loyaltyData,
      ...telemetryData,
    };
  }, [
    isLoggedIn,
    isStripePaymentsSdkEnabled,
    dynamicTips,
    fetchCart,
    fetchPaymentMethods,
    fetchDefaultPaymentMethod,
    fetchCredits,
    fetchGiftCardBalance,
    fetchTelemetryData,
    fetchLoyaltyData,
    fetchBagPaymentInfo,
  ]);

  return useOrdering<PartialLineItem, DeliveryOrderDetail>({
    isLoggedIn,
    isExpressPaymentsEnabled,
    bagTracking,
    navigatePostOrder,
    fetchInitialData,
    fetchRewards,
    fetchWantedTimes,
    addUpsellToBag,
    updateLineItem,
    changeChannelOrLocation,
    requestLogin,
    applyReward,
    removeReward,
    applyPromoCode,
    addPaymentMethod,
    setDefaultPaymentMethod,
    applyGiftCard,
    submitOrder,
    onPostOrder,
    handleOrderSubmissionResponse,
  });
};

type UseOrderStateParams = {
  isStripePaymentsSdkEnabled: boolean;
  isExpressPaymentsEnabled: boolean;
  handleOrderSubmissionResponse: (
    context: OrderingContext<PartialLineItem, DeliveryOrderDetail>,
    event: { data: { success: boolean; isTimeslotUnavailable: boolean } },
  ) => void;
};
